import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { Icon } from '../../../../components/ui/Icon';
import './SettingsButton.css';

const SettingsButton: React.FC = () => {
  const [show, setShow] = useState(false);
  const { theme, setTheme, fontSize, setFontSize } = useSettings();

  const themeOptions = [
    { value: 'light' as const, label: 'ಬೆಳಕು', icon: 'sun-fill' },
    { value: 'dark' as const, label: 'ಕತ್ತಲೆ', icon: 'moon-stars-fill' },
    { value: 'sepia' as const, label: 'ಸೀಪಿಯಾ', icon: 'book-fill' },
  ];

  const fontSizePresets = [
    { value: 14, label: 'Small (14px)' },
    { value: 16, label: 'Default (16px)' },
    { value: 20, label: 'Large (20px)' },
    { value: 24, label: 'XL (24px)' },
  ];

  return (
    <>
      {/* Floating Settings Button */}
      <button
        id="tour-settings-btn"
        className="settings-floating-btn"
        onClick={() => setShow(true)}
        aria-label="Settings"
        title="Settings / ಸಂಯೋಜನೆಗಳು"
      >
        <Icon name="gear-fill" />
      </button>

      {/* Settings Modal using Headless UI */}
      <Transition appear show={show} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShow(false)}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          {/* Modal */}
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <Icon name="sliders" />
                      ಸಂಯೋಜನೆಗಳು / Settings
                    </Dialog.Title>
                    <button
                      onClick={() => setShow(false)}
                      className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Icon name="x-lg" />
                    </button>
                  </div>

                  {/* Theme Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <Icon name="palette-fill" />
                      ಥೀಮ್ / Theme
                    </label>
                    <div className="flex gap-2">
                      {themeOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setTheme(option.value)}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all
                            ${
                              theme === option.value
                                ? 'bg-accent text-white shadow-md'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                          <Icon name={option.icon} />
                          <span className="text-sm">{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Font Size */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <Icon name="fonts" />
                      ಅಕ್ಷರ ಗಾತ್ರ / Font Size: <span className="text-accent">{fontSize}px</span>
                    </label>
                    <input
                      type="range"
                      min="14"
                      max="24"
                      step="1"
                      value={fontSize}
                      onChange={e => setFontSize(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-accent"
                    />
                    <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>
                        <Icon name="type" /> 14px
                      </span>
                      <span>
                        <Icon name="type-h1" /> 24px
                      </span>
                    </div>
                  </div>

                  {/* Preview Text */}
                  <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-semibold">
                      Preview:
                    </p>
                    <p
                      className="text-gray-900 dark:text-gray-100 mb-2"
                      style={{
                        fontSize: `${fontSize}px`,
                        lineHeight: '2',
                        fontFamily: 'var(--font-kannada)',
                      }}
                    >
                      ಭಗವಾನ್ ಗೌತಮ ಬುದ್ಧರ ಬೋಧನೆಗಳು. ಸತ್ಯವನ್ನು ಅರಿಯುವುದೇ ಮೋಕ್ಷ.
                    </p>
                    <p
                      className="text-gray-500 dark:text-gray-400 italic"
                      style={{ fontSize: `${fontSize * 0.85}px` }}
                    >
                      The teachings of Buddha. Understanding truth is liberation.
                    </p>
                  </div>

                  {/* Quick Size Buttons */}
                  <div className="flex flex-wrap gap-2 justify-center mb-6">
                    {fontSizePresets.map(preset => (
                      <button
                        key={preset.value}
                        onClick={() => setFontSize(preset.value)}
                        className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all
                          ${
                            fontSize === preset.value
                              ? 'bg-accent/20 text-accent border border-accent/30'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setShow(false)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <Icon name="x-circle" />
                      ಮುಚ್ಚಿ / Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default SettingsButton;
